import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { 
  Lock, 
  Sparkles, 
  ArrowUpCircle,
  Check
} from 'lucide-react';

const FEATURE_INFO = {
  'teaming': {
    name: 'Teaming Partner Search',
    description: 'Find and vet teaming partners with AI-powered matching based on past performance, certifications, and set-aside requirements.',
    benefits: [
      'AI-powered partner recommendations',
      'Automatic capability gap analysis',
      'NDA and teaming agreement templates',
      'Partner performance tracking'
    ]
  },
  'gap_analysis': {
    name: 'Gap Analysis',
    description: 'Automatically identify capability gaps and compliance risks before you bid.',
    benefits: [
      'Real-time compliance checking',
      'Certification tracking',
      'Experience gap identification',
      'Mitigation recommendations'
    ]
  },
  'proposal_workspace': {
    name: 'Proposal Workspace',
    description: 'Collaborative workspace for proposal development with AI-assisted writing.',
    benefits: [
      'AI-powered section drafting',
      'Compliance matrix automation',
      'Version control & collaboration',
      'Red team review tools'
    ]
  },
  'ai_scoring': {
    name: 'AI pWin Scoring',
    description: 'Get AI-powered probability of win scores with detailed analysis.',
    benefits: [
      'Competitive landscape analysis',
      'Historical win rate data',
      'Risk factor identification',
      'Improvement recommendations'
    ]
  }
};

const GROWTH_PLAN = {
  name: 'Growth',
  price: '$950/mo',
  pursuits: 10,
  users: 10
};

export default function FeatureLockedModal({ 
  isOpen, 
  onClose, 
  featureKey,
  onRequestUpgrade
}) {
  const feature = FEATURE_INFO[featureKey] || {
    name: 'Premium Feature',
    description: 'This feature is available on Growth and Enterprise plans.',
    benefits: []
  };

  const handleUpgradeClick = () => {
    onRequestUpgrade?.();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="feature-locked-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Lock className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <DialogTitle className="text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {feature.name}
              </DialogTitle>
              <Badge className="bg-purple-100 text-purple-700 mt-1">
                <Sparkles className="w-3 h-3 mr-1" />
                Growth Plan Feature
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <p className="text-slate-600 mb-4">
            {feature.description}
          </p>

          {/* Feature Benefits */}
          {feature.benefits.length > 0 && (
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-slate-700 mb-3">What you'll unlock:</p>
              <ul className="space-y-2">
                {feature.benefits.map((benefit, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Upgrade CTA */}
          <div className="border-2 border-purple-200 bg-purple-50/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-900">Upgrade to {GROWTH_PLAN.name}</p>
                <p className="text-sm text-slate-500">
                  {GROWTH_PLAN.pursuits} pursuits • {GROWTH_PLAN.users} users • All Pro features
                </p>
              </div>
              <p className="text-xl font-bold text-purple-700">
                {GROWTH_PLAN.price}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Maybe Later
          </Button>
          <Button 
            className="bg-purple-600 hover:bg-purple-700 text-white"
            onClick={handleUpgradeClick}
            data-testid="upgrade-now-btn"
          >
            <ArrowUpCircle className="w-4 h-4 mr-2" />
            Request Upgrade
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
